// @flow

import React from 'react';
import { localize } from '@oneflowab/pomes';

import extensionSettings from '@app/hocs/extension-settings';

import { checkAcl } from '@flow/libs/utils/acl';
import Divider from '@app/components/divider';
import { EidCountryPicker } from '@app/components/eid-country-picker';
import ExternalLink from '@app/components/icons/external-link';
import { T } from '@app/components/translation';

import { ElectronicId } from './electronic-id';
import { getEidHubList } from './electronic-ids-eid-hub-items';
import { getEidStandAloneList } from './electronic-ids-standalone-items';
import style from './electronic-ids.module.scss';

export type Props = {
  extension: Extension;
  updateExtension: (Extension) => void;
  currentLanguage: EnabledLanguages;
  message: MessageTranslator;
};

type EidKey = string;

export class ElectronicIdsPage extends React.Component<Props> {
  constructor(props) {
    super(props);

    this.state = {
      selectedCountry: '',
      eidList: [],
    };
  }

  componentDidMount() {
    this.updateEidList();
  }

  handleEidToggle = (eidKey: EidKey) => () => {
    const { updateExtension, extension } = this.props;
    const newEidValue = !extension.config[eidKey];
    updateExtension({
      id: extension.extensionId,
      config: {
        ...extension.config,
        [eidKey]: newEidValue,
      },
    });
  };

  onCountryChange = (countryCode: string) => {
    this.setState({ selectedCountry: countryCode }, this.updateEidList);
  };

  updateEidList = () => {
    const { selectedCountry } = this.state;
    const { message } = this.props;

    const updatedList = getEidHubList(message, selectedCountry);

    this.setState({ eidList: updatedList });
  };

  renderEidHub = () => {
    const { currentLanguage, extension } = this.props;
    const { config } = extension;
    const canUpdate = checkAcl(extension.acl, 'extension:config:update', { match: 'any' });
    const items = this.state.eidList;

    const onEidToggle = (signingMethodKey) => {
      this.handleEidToggle(signingMethodKey)();
    };

    const getEids = () => items.map(({
      countries,
      description,
      isEnabled,
      isVisible,
      key,
      title,
      signingMethods,
    }) => (
      <ElectronicId
        canUpdate={canUpdate}
        countries={countries}
        currentLanguage={currentLanguage}
        description={description}
        isChecked={config[key]}
        isEnabled={isEnabled}
        isVisible={isVisible}
        itemKey={key}
        key={key}
        onEidToggle={(signingMethodKey) => onEidToggle(signingMethodKey)}
        title={title}
        signingMethods={signingMethods.map((signingMethod) => ({
          ...signingMethod,
          isChecked: config[signingMethod.key] || false,
        }))}
      />
    ));

    return (
      <>
        <Divider />
        <h4 className={style.Header}>
          <T _str="E-ID Hub" _comment="Header text in Electronic IDs extension page." />
        </h4>
        <div className={style.DescriptionContainer}>
          <div className={style.Description}>
            <p>
              <T
                _str="First, enable eID services that support AdES, QES, or both from the list below. Then, in a template or document, set AdES or QES security level as the default signing method. Participants will choose a preferred eID service during signing, based on the security level you've set and what's enabled"
                _comment="Description text in Electronic IDs extension page."
              />
            </p>
            <p>
              <T
                _str="All prices are per signature."
                _comment="Description text in Electronic IDs extension page."
              />
            </p>
          </div>
          <div className={style.CountryPicker}>
            <EidCountryPicker
              currentLanguage={currentLanguage}
              selectedCountry={this.state.selectedCountry}
              onCountryChange={this.onCountryChange}
            />
          </div>
        </div>
        <div className={style.MainContainer}>{getEids()}</div>
      </>
    );
  };

  renderStandAlone = () => {
    const { currentLanguage, extension, message } = this.props;
    const { config } = extension;
    const canUpdate = checkAcl(extension.acl, 'extension:config:update', { match: 'any' });
    const items = getEidStandAloneList(message);

    const getEids = () => items.map(({
      countries,
      description,
      isEnabled,
      isVisible,
      key,
      title,
      signingMethods,
    }) => (
      <ElectronicId
        canUpdate={canUpdate}
        countries={countries}
        currentLanguage={currentLanguage}
        description={description}
        isChecked={config[key]}
        key={key}
        itemKey={key}
        isEnabled={isEnabled}
        isVisible={isVisible}
        onEidToggle={this.handleEidToggle(key)}
        title={title}
        signingMethods={signingMethods.map((signingMethod) => ({
          ...signingMethod,
          isChecked: config[signingMethod.key] || false,
        }))}
      />
    ));

    return (
      <>
        <div className={style.DescriptionContainer}>
          <div className={style.Description}>
            <T
              _str="Configure how electronic ID (eID) services are used in your documents — either by selecting Stand-alone eID services or allowing participants to choose one from the eID Hub during signing."
              _comment="Description for use of eID signing"
            />
          </div>
        </div>
        <h4 className={style.Header}>
          <T _str="Stand-alone eIDs (legacy)" _comment="Header for stand-alone electronic IDs" />
        </h4>
        <div className={style.DescriptionContainer}>
          <div className={style.Description}>
            <T
              _str="These electronic ID services are added as stand-alone signing methods in templates or documents when you want participants to use a specific eID service."
              _comment="Description for stand-alone electronic IDs"
            />
          </div>
        </div>
        <div className={style.DescriptionContainer}>
          <div className={style.Description}>
            <p>
              <strong>
                <T _str="Note:" _comment="Note for stand-alone electronic IDs" />
              </strong>
              {' '}
              <T
                _str="These stand-alone eID services will only be available under the eID Hub in the future."
                _comment="Note for stand-alone electronic IDs"
              />
            </p>
            <p>
              <T
                _str="All prices are per signature."
                _comment="price for stand-alone electronic IDs"
              />
            </p>
          </div>
        </div>
        <div className={style.MainContainer}>{getEids()}</div>
      </>
    );
  };

  getPricelistLink = (url: string) => (
    <a className={style.ExternalLink} href={url} target="_blank" rel="noopener noreferrer">
      <T
        _str="See the Electronic IDs pricing"
        _comment="Used as the text of for the electronic price list link"
      />
      <ExternalLink height="14px" className={style.ExternalLinkIcon} />
    </a>
  );

  renderBillingInfo = () => (
    <>
      <h4 className={style.SectionHeader}>
        <T _str="Billing" _comment="Header for billing" />
      </h4>
      <div className={style.DescriptionContainer}>
        <div className={style.Description}>
          <T
            _str="Your account is billed quarterly based on the number of Electronic ID signatures used. If you enable both signature levels (AdES and QES) for the same eID service, you may be charged for each level used, depending on actual usage."
            _comment="Description for price list"
          />
        </div>
      </div>
      <a className={style.ExternalLink} href="https://oneflow.com/pricing/electronic-ids" rel="noopener noreferrer" target="_blank">
        <T
          _str="See the Electronic IDs pricing"
          _comment="Used as the text of for the electronic price list link"
        />
        <ExternalLink height="14px" className={style.ExternalLinkIcon} />
      </a>
    </>
  );

  render() {
    return (
      <div>
        <T
          _str="How eIDs are used for signing"
          _comment="Extension configuration header"
        />
        {this.renderStandAlone()}
        {this.renderEidHub()}
        {this.renderBillingInfo()}
      </div>
    );
  }
}

export default localize<Props>(extensionSettings(ElectronicIdsPage));
